// React imports
import React, { FC, useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

// Redux
import { useUpdatePostCategoryByIdMutation } from '../../store/postsCategories/postsCategoryApi';

// Material-UI icons
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

// Custom utility functions and hooks
import { showErrorNotify } from '../helpers/showErrorNotify';
import { showSuccessNotify } from '../helpers/showSuccessNotify';
import { SortOrder } from '../../interfaces/requestParams';
import { postsCategoriesTabHeaders } from './constants';

// Styles
import styles from './styles.module.scss';

// Types
import { IPostCategory } from '../../store/postsCategories/postsCategoryTypes';
import PostCategoryRow from './PostCategoryRow';

interface WorkspaceProps {
  initialItems: IPostCategory[];
  disableDrag?: boolean;
  onDeletePostCategory: (postCategoryId: string) => void;
  sortCell: string | null;
  setSortCell?: (cell: string | null) => void;
  sortDirection?: SortOrder;
  setSortDirection: (direction: SortOrder) => void;
  isLoadingDeletePostsCategory: boolean;
  handlerGetAllPostCategories: () => void;
}

export const PostsCategoriesWorkspace: FC<WorkspaceProps> = ({
  initialItems,
  disableDrag,
  onDeletePostCategory,
  sortCell,
  setSortCell,
  sortDirection,
  setSortDirection,
  isLoadingDeletePostsCategory,
  handlerGetAllPostCategories,
}) => {
  const [items, setItems] = useState<IPostCategory[]>(initialItems);

  const [isUpdating, setIsUpdating] = useState(false);

  const workspaceRef = useRef<HTMLDivElement | null>(null);

  const [updatePostCategoryById, updatePostCategoryByIdResponse] =
    useUpdatePostCategoryByIdMutation();

  const createSortHandler = (row: string) => {
    if (row === sortCell) {
      setSortDirection(
        sortDirection === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
      );
    } else {
      setSortCell && setSortCell(row);
    }
  };

  const renderSortIcon = (column: string) => {
    if (column === sortCell) {
      return sortDirection === SortOrder.ASC ? (
        <ArrowDropUpIcon />
      ) : (
        <ArrowDropDownIcon />
      );
    }
    return null;
  };

  const onDragEnd = async (result: any) => {
    const { destination, source } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const reorderedItems = Array.from(items);
    const [reorderedItem] = reorderedItems.splice(source.index, 1);
    reorderedItems.splice(destination.index, 0, reorderedItem);

    setItems(reorderedItems);

    if (!isUpdating) {
      setIsUpdating(true);

      try {
        await handleOrderUpdate(destination.index, reorderedItem._id);
      } catch (error) {
        console.error('Error updating order:', error);
        setItems(items);
        setIsUpdating(false);
      }
    }
  };

  const handleOrderUpdate = async (order: number, categoryId: string) => {
    await updatePostCategoryById({ categoryData: { order }, categoryId });
  };

  useEffect(() => {
    if (workspaceRef.current) {
      workspaceRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [items]);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    const { data, isSuccess, isError, error } = updatePostCategoryByIdResponse;
    if (isSuccess && data) {
      handlerGetAllPostCategories();
      showSuccessNotify('Post category order successfully updated');
    }
    if (isError && error) {
      showErrorNotify(isError, error);
    }
    setIsUpdating(false);
  }, [updatePostCategoryByIdResponse]);

  if (disableDrag) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.headers}>
          {postsCategoriesTabHeaders.map(header => (
            <div
              key={header.key}
              className={styles.headerCell}
              onClick={() =>
                header.sortable &&
                disableDrag &&
                createSortHandler(header.label)
              }
              style={{
                cursor: header.sortable && disableDrag ? 'pointer' : 'default',
              }}
            >
              {header.label}{' '}
              {disableDrag && header.sortable && renderSortIcon(header.label)}
            </div>
          ))}
        </div>
        <div className={styles.tableBody}>
          {items.map((item, index) => (
            <PostCategoryRow
              postCategory={item}
              onDeletePostCategory={onDeletePostCategory}
              disableDrag={disableDrag}
              isLoadingDeletePostCategory={isLoadingDeletePostsCategory}
            />
          ))}
        </div>
      </div>
    );
  } else {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.headers}>
          {postsCategoriesTabHeaders.map(header => (
            <div
              key={header.key}
              className={styles.headerCell}
              style={{ cursor: 'default' }}
            >
              {header.label}
            </div>
          ))}
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="workspace" direction="vertical">
            {provided => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={styles.tableBody}
              >
                {items.map((item, index) => (
                  <Draggable
                    key={item._id}
                    draggableId={item._id}
                    index={index}
                  >
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <PostCategoryRow
                          postCategory={item}
                          onDeletePostCategory={onDeletePostCategory}
                          isLoadingDeletePostCategory={
                            isLoadingDeletePostsCategory
                          }
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  }
};
